import React, { useState, useEffect, useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  PanelCard, PanelFooter, Section, Choice, NumberField, Toggle, Notice, useReportDirty,
} from './controls';

/** Strip empty strings, NaN and undefined values so Pydantic Optional fields get null instead of "" */
function cleanObj(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v === undefined || (typeof v === 'number' && isNaN(v))) continue;
    out[k] = (typeof v === 'object' && !Array.isArray(v)) ? cleanObj(v) : v;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Key-order-independent comparison, so "dirty" tracks values rather than object shape. */
function stableJson(value: any): string {
  if (value == null || typeof value !== 'object') return JSON.stringify(value ?? null);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  const keys = Object.keys(value).filter((k) => value[k] !== undefined).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableJson(value[k])}`).join(',')}}`;
}

/** Generate standard bitrate options within the camera's supported range */
function generateBitrateOptions(min: number, max: number): number[] {
  const standard = [64, 86, 128, 192, 256, 384, 512, 768, 1024, 1536, 2048, 3072, 4096, 5120, 6144, 8192, 10240, 12288, 16384];
  return standard.filter((b) => b >= min && b <= max);
}

/** Camera sample rates come back in kHz on some models and Hz on others. */
function formatSampleRate(rate: any): string {
  const value = Number(rate);
  if (!value) return String(rate);
  return value >= 1000 ? `${value / 1000} kHz` : `${value} kHz`;
}

/**
 * Storage a stream costs at its configured bitrate. Encoding choices on this page are the
 * single biggest driver of how long the retention window actually lasts, and "4096 kbps"
 * doesn't tell anyone that — "≈42 GB/day" does.
 */
function dailyStorage(bitrateKbps: any): string | null {
  const kbps = Number(bitrateKbps);
  if (!kbps || Number.isNaN(kbps)) return null;
  const gbPerDay = (kbps * 86400) / 8 / 1_000_000;
  return gbPerDay >= 10 ? `${Math.round(gbPerDay)} GB/day` : `${gbPerDay.toFixed(1)} GB/day`;
}

/** One-line summary of a stream, for the collapsed section header. */
function streamSummary(vals: any): string {
  const parts = [vals?.codec, vals?.resolution, vals?.fps ? `${vals.fps} fps` : null,
    vals?.bitrate_kbps ? `${vals.bitrate_kbps} kbps` : null].filter(Boolean);
  const storage = dailyStorage(vals?.bitrate_kbps);
  return parts.length ? `${parts.join(' · ')}${storage ? ` ≈ ${storage}` : ''}` : 'Not reported';
}

function StreamFields({ vals, options, onChange, disabled }: any) {
  const update = (k: string, v: any) => onChange({ ...vals, [k]: v });
  const resolutions = options?.resolutions || [];
  const fpsRange = options?.fps_range || { min: 1, max: 30 };
  const qualityRange = options?.quality_range || { min: 1, max: 6 };
  const bitrateRange = options?.bitrate_range || { min: 128, max: 16384 };
  const codecsSupported = options?.codecs_supported || ['H264', 'H265'];
  // GOP size: keyframe interval in frames. Fall back to the ONVIF-typical 1–250 when the
  // camera doesn't publish a GovLengthRange, so the field is still usable.
  const govRange = options?.gov_length_range || { min: 1, max: 250 };
  const codecProfiles: string[] = (String(vals.codec || '').toUpperCase().includes('265')
    ? options?.h265_profiles
    : options?.h264_profiles) || [];

  const bitrateOptions = generateBitrateOptions(bitrateRange.min, bitrateRange.max);
  // A keyframe interval much longer than the frame rate delays playback start noticeably.
  const gopSeconds = vals.gov_length && vals.fps ? Number(vals.gov_length) / Number(vals.fps) : null;

  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Choice label="Codec" value={vals.codec || null} options={codecsSupported}
          onChange={(v: string) => update('codec', v)} disabled={disabled} />
        <Choice label="Resolution" value={vals.resolution || null} options={resolutions}
          onChange={(v: string) => update('resolution', v)} disabled={disabled} />
        <NumberField label="Frame Rate" hint={`(${fpsRange.min}–${fpsRange.max} fps)`}
          value={vals.fps ?? null} range={fpsRange}
          onChange={(v: number) => update('fps', v)} disabled={disabled} />
        <NumberField label="Quality" hint={`(${qualityRange.min}–${qualityRange.max})`}
          value={vals.quality ?? null} range={qualityRange}
          onChange={(v: number) => update('quality', v)} disabled={disabled} />
        <Choice
          label="Bitrate" hint="(kbps)" value={vals.bitrate_kbps || null}
          options={bitrateOptions}
          labels={bitrateOptions.map((b) => `${b} — ≈${dailyStorage(b)}`)}
          onChange={(v: string) => update('bitrate_kbps', Number(v))} disabled={disabled}
        />
        <Choice label="Bitrate Type" value={vals.bitrate_type || null} options={['CBR', 'VBR']}
          labels={['CBR — constant', 'VBR — variable']}
          onChange={(v: string) => update('bitrate_type', v)} disabled={disabled} />
        <NumberField label="GOP / I-Frame Interval" hint={`(${govRange.min}–${govRange.max} frames)`}
          value={vals.gov_length ?? null} range={govRange}
          onChange={(v: number) => update('gov_length', v)} disabled={disabled} />
        {codecProfiles.length > 0 && (
          <Choice label="Codec Profile" value={vals.codec_profile || null} options={codecProfiles}
            onChange={(v: string) => update('codec_profile', v)} disabled={disabled} />
        )}
      </div>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        A larger GOP lowers bitrate but delays how quickly a viewer or a recording can start
        {gopSeconds ? ` — this one puts a keyframe every ${gopSeconds.toFixed(1)}s` : ''}.
      </p>
    </div>
  );
}

export default function EncodingPanel({ config, capabilities, onSave, loading, onDirtyChange }: any) {
  const [mainstream, setMainstream] = useState<any>({});
  const [substream, setSubstream] = useState<any>({});
  const [audio, setAudio] = useState<any>({});

  const reset = () => {
    setMainstream(config?.mainstream ? { ...config.mainstream } : {});
    setSubstream(config?.substream ? { ...config.substream } : {});
    setAudio(config?.audio ? { ...config.audio } : {});
  };

  useEffect(reset, [config]);

  const audioCaps: any = capabilities?.audio || {};
  // `supported: false` means the camera reported no audio input at all — different from audio
  // being present but currently switched off, which is what `enabled` tracks.
  const audioSupported = audioCaps.supported !== false;
  const audioEncodings = audioCaps.encodings?.length ? audioCaps.encodings : ['G711', 'AAC'];
  const sampleRates = audioCaps.sample_rates?.length ? audioCaps.sample_rates : [8000, 16000];
  const audioEnabled = !!audio.enabled;

  const changed = useMemo(() => {
    const groups: string[] = [];
    if (stableJson(mainstream) !== stableJson(config?.mainstream || {})) groups.push('mainstream');
    if (stableJson(substream) !== stableJson(config?.substream || {})) groups.push('substream');
    if (stableJson(audio) !== stableJson(config?.audio || {})) groups.push('audio');
    return groups;
  }, [mainstream, substream, audio, config]);

  useReportDirty(changed.length > 0, onDirtyChange);

  const submit = () => onSave({
    mainstream: cleanObj(mainstream),
    substream: cleanObj(substream),
    // `enabled` is a real boolean, so it must survive cleanObj's empty-value stripping
    // even when false — otherwise switching audio off would send nothing at all.
    audio: audioSupported ? { ...(cleanObj(audio) || {}), enabled: audioEnabled } : undefined,
  });

  return (
    <PanelCard
      title="Encoding Settings"
      description="Stream format the camera produces — this drives both live quality and storage use."
      footer={
        <PanelFooter
          dirty={changed.length > 0} loading={loading} onReset={reset} onSave={submit}
          dirtyLabel={`Unsaved changes in ${changed.join(', ')}`}
        />
      }
    >
      <Section title="Mainstream" hint={streamSummary(mainstream)} collapsible>
        <StreamFields vals={mainstream} options={capabilities?.mainstream} onChange={setMainstream} disabled={loading} />
      </Section>

      <Section title="Substream" hint={streamSummary(substream)} collapsible defaultOpen={false}>
        <Notice icon={InformationCircleIcon}>
          The substream feeds multi-camera grid views and AI analytics. Keeping it small is what
          makes those views cheap — a low resolution and bitrate here is intentional.
        </Notice>
        <StreamFields vals={substream} options={capabilities?.substream} onChange={setSubstream} disabled={loading} />
      </Section>

      <Section
        title="Audio"
        hint={!audioSupported ? 'No audio input reported' : audioEnabled ? `On · ${audio.codec || 'codec not set'}` : 'Off'}
        collapsible defaultOpen={false}
      >
        {!audioSupported ? (
          <Notice icon={InformationCircleIcon}>This camera did not report an audio input.</Notice>
        ) : (
          <>
            <Toggle
              label="Enable audio" description="Capture the camera's microphone alongside video."
              checked={audioEnabled} disabled={loading}
              onChange={(v: boolean) => setAudio({ ...audio, enabled: v })}
            />
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${audioEnabled ? '' : 'opacity-50'}`}>
              <Choice label="Codec" value={audio.codec || null} options={audioEncodings}
                onChange={(v: string) => setAudio({ ...audio, codec: v })} disabled={loading || !audioEnabled} />
              <Choice label="Sample Rate" value={audio.sampling_freq || null} options={sampleRates}
                labels={sampleRates.map((r: any) => formatSampleRate(r))}
                onChange={(v: string) => setAudio({ ...audio, sampling_freq: v })} disabled={loading || !audioEnabled} />
              {audioCaps.bitrates?.length > 0 && (
                <Choice label="Bitrate" hint="(kbps)" value={audio.bitrate_kbps || null} options={audioCaps.bitrates}
                  onChange={(v: string) => setAudio({ ...audio, bitrate_kbps: Number(v) })}
                  disabled={loading || !audioEnabled} />
              )}
            </div>
          </>
        )}
      </Section>
    </PanelCard>
  );
}
